import { forceInt } from '@curbnturf/helpers';
import { DateTime } from 'luxon';
import { DEFAULT_CHECK_IN_TIME, DEFAULT_CHECK_OUT_TIME } from '../constants';
import { RatingType } from '../enums/rating-types';
import { ICalendarDay } from '../interfaces/c-date';
import { IListing } from '../interfaces/listing';
import { IRating } from '../interfaces/rating';
import { ISite } from '../interfaces/site';

export function containsCompleteRating(ratings?: IRating[]): boolean {
  return !!ratings?.find((rating) => rating.topic === RatingType.overall && rating.score.description);
}

export function isDateValidForSite(date: string, site: ISite, listing: IListing): boolean {
  if (!site || !listing) {
    return false;
  }

  // Filter out Blackout Days - Listing
  if (listing.blackoutDays && listing.blackoutDays.length > 0) {
    for (const blackoutDateRange of listing.blackoutDays) {
      if (blackoutDateRange.includes(':')) {
        const blackoutSplit = blackoutDateRange.split(':');
        const blackoutDateStart = DateTime.fromFormat(blackoutSplit[0], 'yyyy-MM-dd');
        const blackoutDateEnd = DateTime.fromFormat(blackoutSplit[1], 'yyyy-MM-dd');
        if (DateTime.fromISO(date) >= blackoutDateStart && DateTime.fromISO(date) <= blackoutDateEnd) {
          return false;
        }
      } else {
        const blackoutDate = DateTime.fromFormat(blackoutDateRange, 'yyyy-MM-dd');
        if (DateTime.fromISO(date) === blackoutDate) {
          return false;
        }
      }
    }
  }

  // Filter out Blackout Months - Listing
  if (listing.blackoutMonths && listing.blackoutMonths.length > 0) {
    for (const blackoutMonth of listing.blackoutMonths) {
      if (DateTime.fromISO(date).month === blackoutMonth) {
        return false;
      }
    }
  }

  // Filter out Blackout Months - Day of the Week
  if (listing.blackoutDayOfWeek && listing.blackoutDayOfWeek.length > 0) {
    for (const blackoutDayOfWeek of listing.blackoutDayOfWeek) {
      if (DateTime.fromISO(date).weekdayLong?.toLowerCase() === blackoutDayOfWeek.toLowerCase()) {
        return false;
      }
    }
  }

  // Filter out Blackout Days - Site
  if (site.blackoutDays && site.blackoutDays.length > 0) {
    for (const blackoutDateRange of site.blackoutDays) {
      if (blackoutDateRange.includes(':')) {
        const blackoutSplit = blackoutDateRange.split(':');
        const blackoutDateStart = DateTime.fromFormat(blackoutSplit[0], 'yyyy-MM-dd');
        const blackoutDateEnd = DateTime.fromFormat(blackoutSplit[1], 'yyyy-MM-dd');
        if (DateTime.fromISO(date) >= blackoutDateStart && DateTime.fromISO(date) <= blackoutDateEnd) {
          return false;
        }
      } else {
        const blackoutDate = DateTime.fromFormat(blackoutDateRange, 'yyyy-MM-dd');
        if (DateTime.fromISO(date) === blackoutDate) {
          return false;
        }
      }
    }
  }

  // Filter out Blackout Months - Site
  if (site.blackoutMonths && site.blackoutMonths.length > 0) {
    for (const blackoutMonth of site.blackoutMonths) {
      if (DateTime.fromISO(date).month === blackoutMonth) {
        return false;
      }
    }
  }

  const reserveDaysInAdvance = forceInt(listing.reserveDaysInAdvance || 0);

  switch (reserveDaysInAdvance) {
    // if reserveDaysInAdvance = 0 then the can check in up until the check out time
    case 0: {
      return (
        DateTime.fromISO(`${date}T${listing.checkOut || DEFAULT_CHECK_OUT_TIME}`, {
          zone: listing.timezone || undefined,
        }).plus({
          days: 1,
        }) > DateTime.now()
      );
    }

    // by default if there is a reserveDaysInAdvance it is based of the checkIn
    default: {
      return (
        DateTime.fromISO(`${date}T${listing.checkIn || DEFAULT_CHECK_IN_TIME}`, { zone: listing.timezone || undefined }) >
        DateTime.now().plus({ days: reserveDaysInAdvance })
      );
    }
  }
}

export function filterAvailableDays(availableDays: ICalendarDay[], site: ISite, listing: IListing): ICalendarDay[] {
  return availableDays.filter((day) => isDateValidForSite(day.date, site, listing));
}
